import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/shared/foundation/primitives/Button";

import {
  type Filter,
  filterSchema,
  isValueless,
  type NumericComparator,
  numericComparators,
} from "./FilterSchema";
import styles from "./MetricFilterForm.module.css";
import { OperatorPicker } from "./OperatorPicker";
import { NumericValueInput } from "./valueInputs/NumericValueInput";

export interface MetricFilterOption {
  /** The metric's bare dotted path (e.g. "navigation.path_deviation"). */
  name: string;
  /** Unit of measure (e.g. "%", "ms") **/
  unit: string | null;
}

export interface MetricFilterFormProps {
  /** The selected metric (name + unit) */
  metric: MetricFilterOption;
  /** Fired on Apply with the committed `metric` filter. */
  onCommit: (filter: Filter) => void;
  /** Fired when the user clicks Cancel. */
  onCancel: () => void;
  /**
   * Seeds the form with an existing metric filter's comparator and values
   * (the chip-edit flow). Its `field` must equal `metric.name`. When absent, the
   * form starts blank with the GREATER_THAN default (the add flow).
   */
  initialFilter?: Filter;
  /** Submit button label. Defaults to `"Apply"`; the edit popover passes
   *  `"Update filter"`. */
  submitLabel?: string;
}

/**
 * Right pane of the metrics filter: a metrics-specific header (muted path prefix
 * + bold leaf, inline and wrap-capable) over the reused numeric operator picker
 * and value input.
 */
export function MetricFilterForm({
  metric,
  onCommit,
  onCancel,
  initialFilter,
  submitLabel = "Apply",
}: MetricFilterFormProps) {
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: initialFilter ?? {
      type: "metric",
      field: metric.name,
      comparator: "GREATER_THAN",
      values: [],
      unit: metric.unit,
    },
    // Continuous validation so the Apply button reflects validity live as the
    // user fills the value input.
    mode: "onChange",
  });

  const filter = form.watch();

  const setValues = (next: Filter["values"]) =>
    form.setValue("values", next, { shouldValidate: true, shouldDirty: true });

  const lastDot = metric.name.lastIndexOf(".");
  const pathPrefix = lastDot === -1 ? "" : metric.name.slice(0, lastDot + 1); // incl. trailing dot
  const leaf = lastDot === -1 ? metric.name : metric.name.slice(lastDot + 1);

  return (
    <form
      className={styles.panel}
      autoComplete="off"
      onSubmit={(event) => {
        void form.handleSubmit((values) => onCommit(values))(event);
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h3 className={styles.title} title={metric.name}>
            {pathPrefix && <span className={styles.path}>{pathPrefix}</span>}
            <span className={styles.leaf}>{leaf}</span>
          </h3>
          {metric.unit && (
            <span className={styles.unit} title={`Unit: ${metric.unit}`}>
              {metric.unit}
            </span>
          )}
        </header>

        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType="numeric"
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  // Clear the value if a user selects a valueless operator
                  if (isValueless(next)) {
                    setValues([]);
                  }
                }}
                // Metric reuses the numeric operator set (see `fieldType` above).
                operators={numericComparators}
                // Numeric has no presets, so neither does metric.
                presets={[]}
              />
            )}
          />
        </section>

        {!isValueless(filter.comparator) && (
          <section className={styles.section} aria-label="Value">
            <h4 className={styles.sectionLabel}>Value</h4>
            {/* The metric variant is numeric-shaped, so its comparator/values are
                always numeric; narrow for the numeric input.

                TODO: The form type should narrow for us.
              */}
            <NumericValueInput
              comparator={filter.comparator as NumericComparator}
              value={filter.values as number[]}
              onChange={setValues}
              unit={metric.unit}
            />
          </section>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid}
          className={styles.footerAction}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
